import { useState } from "react";
import { apiFetch } from "@/lib/api-client";

/**
 * D2: 10DLC Campaign Registration
 * Campaign types: marketing, transactional, 2FA.
 * Sample messages required. Submit to TCR. Status tracking.
 */

const USE_CASES = [
  { value: "MARKETING", label: "Marketing", description: "Promotional messages, offers, newsletters" },
  { value: "CUSTOMER_CARE", label: "Customer Care", description: "Support conversations, appointment reminders" },
  { value: "2FA", label: "Two-Factor Authentication", description: "Verification codes and OTPs" },
  { value: "ACCOUNT_NOTIFICATION", label: "Account Notifications", description: "Order updates, shipping, billing" },
  { value: "DELIVERY_NOTIFICATION", label: "Delivery Notifications", description: "Package tracking and delivery updates" },
] as const;

interface CampaignData {
  id: string;
  useCase: string;
  description: string;
  status: string;
  throughputLimit: number | null;
  rejectionReason: string | null;
}

export default function CampaignRegistrationPage() {
  const [campaigns, setCampaigns] = useState<CampaignData[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form
  const [brandId, _setBrandId] = useState("");
  const [useCase, setUseCase] = useState("MARKETING");
  const [description, setDescription] = useState("");
  const [sampleMessages, setSampleMessages] = useState(["", ""]);
  const [helpMessage, setHelpMessage] = useState("Reply HELP for help. Msg&data rates may apply.");
  const [optOutMessage, setOptOutMessage] = useState("Reply STOP to unsubscribe. You will no longer receive messages.");

  const updateSample = (idx: number, value: string) => {
    const updated = [...sampleMessages];
    updated[idx] = value;
    setSampleMessages(updated);
  };

  const addSample = () => {
    if (sampleMessages.length < 5) setSampleMessages([...sampleMessages, ""]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await apiFetch<{ data: CampaignData }>("/v1/sms-registration/campaigns", {
        method: "POST",
        body: JSON.stringify({
          brandId,
          useCase,
          description,
          sampleMessages: sampleMessages.filter(Boolean),
          helpMessage,
          optOutMessage,
        }),
      });
      setCampaigns((prev) => [result.data, ...prev]);
      setShowForm(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to submit campaign");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">10DLC Campaigns</h1>
        <button onClick={() => setShowForm(true)} className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground">
          + Register Campaign
        </button>
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-[var(--color-destructive)]/20 bg-[var(--color-destructive)]/5 p-4 text-sm text-[var(--color-destructive)]">{error}</div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 space-y-4 rounded-lg border border-border bg-card p-4">
          <h3 className="font-medium">New Campaign Registration</h3>

          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Use Case *</label>
            <div className="grid gap-2 md:grid-cols-2">
              {USE_CASES.map((uc) => (
                <label key={uc.value} className={`cursor-pointer rounded-md border p-3 text-sm ${useCase === uc.value ? "border-primary bg-primary/5" : "border-border"}`}>
                  <input type="radio" name="useCase" value={uc.value} checked={useCase === uc.value} onChange={(e) => setUseCase(e.target.value)} className="sr-only" />
                  <p className="font-medium">{uc.label}</p>
                  <p className="text-xs text-muted-foreground">{uc.description}</p>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Use Case Description *</label>
            <textarea required value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe how you will use SMS for this campaign..." className="w-full rounded-md border border-input p-2 text-sm" rows={3} />
          </div>

          <div>
            <label className="mb-1 block text-xs text-muted-foreground">Sample Messages (min 2, max 5) *</label>
            {sampleMessages.map((msg, idx) => (
              <input key={idx} value={msg} onChange={(e) => updateSample(idx, e.target.value)} placeholder={`Sample message ${idx + 1}...`} className="mb-2 w-full rounded-md border border-input px-3 py-2 text-sm" />
            ))}
            {sampleMessages.length < 5 && (
              <button type="button" onClick={addSample} className="text-xs text-primary hover:underline">+ Add another sample</button>
            )}
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">HELP Reply Message *</label>
              <input required value={helpMessage} onChange={(e) => setHelpMessage(e.target.value)} className="w-full rounded-md border border-input px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="mb-1 block text-xs text-muted-foreground">STOP Reply Message *</label>
              <input required value={optOutMessage} onChange={(e) => setOptOutMessage(e.target.value)} className="w-full rounded-md border border-input px-3 py-2 text-sm" />
            </div>
          </div>

          <div className="flex gap-2">
            <button type="submit" disabled={isSubmitting} className="rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground disabled:opacity-50">
              {isSubmitting ? "Submitting..." : "Submit Campaign"}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="rounded-md border border-border px-4 py-2 text-sm">Cancel</button>
          </div>
        </form>
      )}

      {/* Campaigns list */}
      <div className="rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50 text-left">
              <th className="px-4 py-3 font-medium">Use Case</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Throughput</th>
              <th className="px-4 py-3 font-medium">Details</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">No campaigns registered yet.</td></tr>
            )}
            {campaigns.map((c) => (
              <tr key={c.id} className="border-b border-border last:border-0">
                <td className="px-4 py-3 font-medium">{c.useCase}</td>
                <td className="px-4 py-3">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                    c.status === "registered" ? "bg-[var(--color-success)]/10 text-[var(--color-success)]" :
                    c.status === "rejected" ? "bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]" :
                    "bg-[var(--color-warning)]/10 text-[var(--color-warning)]"
                  }`}>{c.status}</span>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{c.throughputLimit ? `${c.throughputLimit} msg/s` : "—"}</td>
                <td className="px-4 py-3 text-xs text-muted-foreground">{c.rejectionReason ?? c.description.slice(0, 50)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
